import { useState } from 'react';
import {
  Card, CardContent, Stack, Switch, Typography, Box, Chip, Alert, Snackbar, Divider,
} from '@mui/material';
import { Layers, Bolt } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { verticalsApi, type BusinessVertical } from '../../api/verticals.api';
import { extractApiError } from '../../api/error';

/**
 * Super Admin only (business_vertical.golive): phased go-live control. Flip a
 * business vertical Live (active) / Off (inactive). An "Off" vertical disappears
 * from every user's switcher and menu — its data is untouched and reappears the
 * instant it is switched back Live. No deploy/migration involved.
 */
export function VerticalGoLivePage() {
  const qc = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: verticals } = useQuery({
    queryKey: ['verticals', 'manage'],
    queryFn: () => verticalsApi.listAllForManage(),
  });

  const mut = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'active' | 'inactive' }) => verticalsApi.setStatus(id, status),
    onSuccess: (v) => {
      setError(null);
      setToast(`${v.name} is now ${v.status === 'active' ? 'LIVE' : 'OFF'}`);
      qc.invalidateQueries({ queryKey: ['verticals'] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const liveCount = (verticals ?? []).filter((v) => v.status === 'active').length;

  return (
    <>
      <PageHeader
        title="Vertical Go-Live Control"
        subtitle="Super Admin only. Switch a business vertical Live or Off. Off hides it from all users; data is preserved and returns instantly when switched back Live."
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Turning a vertical <strong>Off</strong> never deletes any data — it only hides the vertical's menu, switcher entry and dashboards. Switch it back to <strong>Live</strong> any time to resume exactly where it left off.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            <Layers fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
            Business Verticals — {liveCount} live
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <Stack divider={<Divider flexItem />}>
            {(verticals ?? []).map((v: BusinessVertical) => {
              const live = v.status === 'active';
              return (
                <Box key={v.id} sx={{ display: 'flex', alignItems: 'center', py: 1.25, gap: 1 }}>
                  <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: live ? 'success.main' : 'action.disabledBackground', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Bolt fontSize="small" />
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography fontWeight={600} noWrap>{v.name}</Typography>
                    {v.description && <Typography variant="caption" color="text.secondary" noWrap display="block">{v.description}</Typography>}
                  </Box>
                  <Chip size="small" color={live ? 'success' : 'default'} label={live ? 'LIVE' : 'OFF'} sx={{ fontWeight: 700 }} />
                  <Switch
                    checked={live}
                    disabled={mut.isPending}
                    onChange={(e) => mut.mutate({ id: v.id, status: e.target.checked ? 'active' : 'inactive' })}
                    inputProps={{ 'aria-label': `Toggle ${v.name}` }}
                  />
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
